import { companyIdOf } from './workspaceScope';
export type JobRecord = Record<string, any>;

export interface JobContactsRepository {
  listStaff(): Promise<JobRecord[] | null>;
  listLeads(query: string): Promise<JobRecord[]>;
  listReferralAttributions(leadIds: string[]): Promise<JobRecord[]>;
  findCompanyIdByName(name: string): Promise<string | null>;
  createCompany(name: string): Promise<string | null>;
  linkLeadToCompany(leadId: string, companyId: string): Promise<void>;
}

export const isCompanyContact = (contact: JobRecord | null) => contact?.industry !== undefined;

export const buildLeadLookupQuery = (contact: JobRecord, companyProfile: boolean) => {
  const quoted = (value: string) => JSON.stringify(value);
  if (companyProfile) return `company_id.eq.${quoted(String(contact.id))}`;
  const companyId = companyIdOf(contact);
  if (companyId) return `company_id.eq.${quoted(companyId)}`;
  const queryParts = [`id.eq.${quoted(String(contact.id))}`];
  const email = (contact.email || contact.clientEmail || '').trim();
  if (email) queryParts.push(`email.eq.${quoted(email)}`);
  return queryParts.join(',');
};

export const filterDisplayableLeads = (leads: JobRecord[]) =>
  leads.filter(lead => lead.project_ref || lead.event_type || lead.additional_details || lead.venue_location);

export const attachReferralAttributions = (leads: JobRecord[], referrals: JobRecord[]) => {
  const referralByLeadId = new Map(referrals.map(referral => [referral.event_lead_id, {
    partner_programme_type_snapshot: referral.partner_programme_type_snapshot,
    partner_display_name_snapshot: referral.partner_display_name_snapshot,
  }]));
  return leads.map(lead => ({ ...lead, referral_attribution: referralByLeadId.get(lead.id) || null }));
};

/**
 * Preserves a legacy on-load side effect: displayable leads with a company name but
 * no company_id are associated to an existing company, or a new company is created.
 * This intentionally remains sequential and mutates the supplied lead objects just
 * as InquiryDetailsModal did before the Phase 1 extraction.
 */
export const ensureLeadCompanyAssociations = async (
  leads: JobRecord[],
  repository: JobContactsRepository,
) => {
  for (const lead of leads) {
    const companyName = lead.company_name?.trim();
    if (!companyName || lead.company_id || companyName.toLowerCase() === 'private account') continue;

    let companyId = await repository.findCompanyIdByName(companyName);
    if (!companyId) companyId = await repository.createCompany(companyName);
    if (companyId) {
      await repository.linkLeadToCompany(lead.id, companyId);
      lead.company_id = companyId;
    }
  }
  return leads;
};

export const loadJobContactData = async (
  contact: JobRecord,
  companyProfile: boolean,
  repository: JobContactsRepository,
) => {
  const staff = await repository.listStaff();
  const leads = filterDisplayableLeads(await repository.listLeads(buildLeadLookupQuery(contact, companyProfile)));
  const referrals = leads.length > 0
    ? await repository.listReferralAttributions(leads.map(lead => lead.id))
    : [];
  // Viewing a workspace must never infer or mutate company membership.
  return { staff, leads: attachReferralAttributions(leads, referrals) };
};

export const getSidebarContacts = (
  activeContact: JobRecord | null,
  contactCompanyId: string | null | undefined,
  crmItems: JobRecord[],
) => {
  if (!activeContact) return [];
  if (!contactCompanyId) return [activeContact];
  const unique = new Map<string, JobRecord>();
  crmItems
    .filter(item => item.company_id === contactCompanyId || item.companyId === contactCompanyId)
    .forEach(contact => {
      const key = String(contact.email || contact.clientEmail || contact.id).trim().toLowerCase();
      if (!unique.has(key)) unique.set(key, contact);
    });
  return Array.from(unique.values());
};

export const getActiveQuotes = (
  activeContact: JobRecord | null,
  companyProfile: boolean,
  quotes: JobRecord[],
) => {
  if (!activeContact) return [];
  return quotes.filter(quote => {
    if (companyProfile) return quote.company_id === activeContact.id || quote.companyId === activeContact.id;
    const email = (activeContact.email || activeContact.clientEmail || '').toLowerCase().trim();
    const quoteEmail = (quote.client?.email || quote.client_email || '').toLowerCase().trim();
    return Boolean(email && quoteEmail === email);
  }).sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
};

export const sortJobShifts = (shifts: JobRecord[]) => [...shifts].sort((a, b) => {
  const dateA = a.event_date ? new Date(a.event_date).getTime() : 9999999999999;
  const dateB = b.event_date ? new Date(b.event_date).getTime() : 9999999999999;
  if (dateA !== dateB) return dateA - dateB;
  return (a.setup_time || a.start_time || '99:99').localeCompare(b.setup_time || b.start_time || '99:99');
});

export const getShiftCrewState = (shift: JobRecord) => {
  const crew = shift.staff_roster?.crew || [];
  const requiredCount = shift.staff_roster?.required_count || 1;
  const confirmedCount = crew.filter((member: JobRecord) => member.status === 'CONFIRMED').length;
  const acceptedCount = crew.filter((member: JobRecord) => member.status === 'ACCEPTED').length;
  const pendingCount = crew.filter((member: JobRecord) => member.status === 'PENDING').length;
  const shortStaffed = confirmedCount < requiredCount;
  const needsApproval = acceptedCount > 0;
  const awaitingReply = pendingCount > 0;
  return {
    crew,
    requiredCount,
    confirmedCount,
    acceptedCount,
    pendingCount,
    shortStaffed,
    needsApproval,
    awaitingReply,
    forceOpen: needsApproval || (shortStaffed && !awaitingReply),
  };
};
